import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectIsAuthenticated } from '../app/redux/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../app/redux/store';
import { useTranslation } from 'react-i18next';
import { logger } from '../app/utils/logger';
import { SERVER_REGION } from '../i18n';
import { 
  fetchUserInfo,
  getScrollInfoV2,
  startAsyncMonthlyReport,
  startTeamsDataExport,
  checkAsyncJobStatus,
  downloadAsyncReport,
  selectAsyncReportState,
  selectTeamsExportState,
  selectCurrentTeam,
  resetAsyncReport,
  resetTeamsExport,
  setAsyncReportError,
  setTeamsExportError
} from '../app/redux/slices/dataSlice';
import Navigation from '../app/components/NavigationSPA';

interface DownloadFilters {
  dateRange: 'all' | 'monthly';
  selectedMonth: string; // Keep for backward compatibility if needed
  // For monthly reports (now all async with charts)
  asyncMonth: string;
  timeZone?: string;
}

interface ProbeItem {
  trapId?: string;
  probeId?: string;
  id?: string;
  [key: string]: unknown;
}

export default function DataDownloadPage() {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentTeam = useSelector(selectCurrentTeam);
  const asyncReportState = useSelector(selectAsyncReportState);
  const teamsExportState = useSelector(selectTeamsExportState);
  const userInfo = useSelector((state: { data: { userInfo: Record<string, unknown> | null } }) => state.data.userInfo) as Record<string, unknown> | null;
  const scrollInfo = useSelector((state: RootState) => state.data.scrollInfo);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation(['dataDownload', 'common']);
  const [filters, setFilters] = useState<DownloadFilters>({
    dateRange: 'all',
    selectedMonth: '', // Will be set from userInfo.monthListSinceJoined
    asyncMonth: '',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  // Function to get translated month names
  const getMonthNames = (): string[] => {
    return [
      t('common:monthJan'),
      t('common:monthFeb'),
      t('common:monthMar'),
      t('common:monthApr'),
      t('common:monthMay'),
      t('common:monthJun'),
      t('common:monthJul'),
      t('common:monthAug'),
      t('common:monthSep'),
      t('common:monthOct'),
      t('common:monthNov'),
      t('common:monthDec')
    ];
  };

  // Function to convert month string to English format for API
  // This ensures the backend always receives English format (e.g., "Dec 2024")
  // even when the UI displays Chinese format (e.g., "12月 2024")
  const convertMonthToEnglish = (monthStr: string): string => {
    if (!monthStr) return monthStr;
    
    // English month abbreviations
    const englishMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Extract year (assumes format is "Month Year" or "月份 年份")
    const parts = monthStr.trim().split(/\s+/);
    if (parts.length < 2) return monthStr; // Invalid format, return as-is
    
    const monthPart = parts[0];
    const yearPart = parts[1];
    
    // Check if it's already in English format
    if (englishMonths.includes(monthPart)) {
      return monthStr; // Already in English, return as-is
    }
    
    // Try to parse Chinese month format (e.g., "12月" -> 12)
    const monthMatch = monthPart.match(/^(\d+)月$/);
    if (monthMatch) {
      const monthNum = parseInt(monthMatch[1], 10);
      if (monthNum >= 1 && monthNum <= 12) {
        return `${englishMonths[monthNum - 1]} ${yearPart}`;
      }
    }
    
    // Fallback: return as-is if we can't convert
    return monthStr;
  };

  // Function to translate backend progress messages
  const translateProgressMessage = (message: string): string => {
    if (!message) return '';
    
    // Match "Processing probe [probeId]..."
    const processingMatch = message.match(/Processing probe\s+(\S+)/i);
    if (processingMatch) {
      const probeId = processingMatch[1];
      return `${t('dataDownload:processingProbe')} ${probeId}...`;
    }
    
    // Match "Generating report..."
    if (message.match(/Generating report/i)) {
      return `${t('dataDownload:generatingReport')}...`;
    }
    
    // Match "Preparing data..."
    if (message.match(/Preparing data/i)) {
      return `${t('dataDownload:preparingData')}...`;
    }
    
    // Return original message if no match
    return message;
  };

  // Polling for async job status - separate intervals for each job type
  const [monthlyPollingInterval, setMonthlyPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [teamsPollingInterval, setTeamsPollingInterval] = useState<NodeJS.Timeout | null>(null);

  // Set default month for async report
  useEffect(() => {
    if (!filters.asyncMonth) {
      const now = new Date();
      const monthNames = getMonthNames();
      const currentMonth = `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
      
      setFilters(prev => ({
        ...prev,
        asyncMonth: currentMonth,
      }));
    }
  }, [filters.asyncMonth]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Fetch user info to get team data and monthListSinceJoined
    const loadData = async () => {
      await dispatch(fetchUserInfo());
      
      // Fetch scrollInfo for the current team to get probes
      if (currentTeam?.name) {
        await dispatch(getScrollInfoV2({
          teamName: currentTeam.name,
          page: 1,
          pageSize: 1000, // Get all probes
          filter: "id",
          ascend: true
        }));
      }
      
      setIsLoading(false);
    };
    
    loadData();
  }, [isAuthenticated, navigate, dispatch, currentTeam?.name]);


  // Start polling when async job starts
  useEffect(() => {
    if (asyncReportState.isGenerating && asyncReportState.jobId && !monthlyPollingInterval) {
      logger.log('Starting monthly report polling for job:', asyncReportState.jobId);
      const interval = setInterval(() => {
        dispatch(checkAsyncJobStatus(asyncReportState.jobId!));
      }, 2000); // Poll every 2 seconds
      setMonthlyPollingInterval(interval);
    }

    // Stop polling when job completes or fails
    if (!asyncReportState.isGenerating && monthlyPollingInterval) {
      clearInterval(monthlyPollingInterval);
      setMonthlyPollingInterval(null);
    }

    return () => {
      if (monthlyPollingInterval) {
        clearInterval(monthlyPollingInterval);
      }
    };
  }, [asyncReportState.isGenerating, asyncReportState.jobId, monthlyPollingInterval, dispatch]);

  // Teams export polling effect
  useEffect(() => {
    if (teamsExportState.isGenerating && teamsExportState.jobId && !teamsPollingInterval) {
      logger.log('Starting teams export polling for job:', teamsExportState.jobId);
      const interval = setInterval(async () => {
        try {
          await dispatch(checkAsyncJobStatus(teamsExportState.jobId!)).unwrap();
        } catch (error) {
          logger.error('Teams export polling error:', error);
        }
      }, 2000);
      setTeamsPollingInterval(interval);
    } else if (!teamsExportState.isGenerating && teamsPollingInterval) {
      logger.log('Stopping teams export polling');
      clearInterval(teamsPollingInterval);
      setTeamsPollingInterval(null);
    }
  }, [teamsExportState.isGenerating, teamsExportState.jobId, teamsPollingInterval, dispatch]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (monthlyPollingInterval) {
        clearInterval(monthlyPollingInterval);
      }
      if (teamsPollingInterval) {
        clearInterval(teamsPollingInterval);
      }
    };
  }, [monthlyPollingInterval, teamsPollingInterval]);

  const handleDownload = async () => {
    if (!currentTeam?.name) {
      logger.log('No team available for download:', currentTeam);
      return;
    }

    if (!scrollInfo?.traps || scrollInfo.traps.length === 0) {
      logger.error('No probes available for download');
      alert('No probe data available for download. Please ensure you have probes configured.');
      return;
    }

    logger.log('Starting download with team:', currentTeam.name);
    logger.log('Selected month:', filters.selectedMonth);
    logger.log('Available months:', (userInfo as { monthListSinceJoined?: string[] })?.monthListSinceJoined);
    try {
      // Extract probe data and ensure numeric IDs from scrollInfo.traps
      const rawProbes = scrollInfo?.traps || [];
      const probeIds = rawProbes
        .map((probe) => {
          // Try different possible ID fields
          const probeItem = probe as unknown as ProbeItem;
          const id = probeItem.trapId || probeItem.probeId || probeItem.id;
          // Convert to string to ensure quotations
          return id ? String(id) : null;
        })
        .filter((id: string | null): id is string => id !== null); // Only keep valid IDs
      
      // Option 1: Simple array of IDs 
      const option1 = probeIds;
      
      // Option 2: Array of objects with probeId (with string IDs)
      const option2 = probeIds.map((id: string) => ({ probeId: id }));
      
      // Option 3: Just use the raw probes data as-is
      const option3 = rawProbes;
      
      // Option 4: Exactly match your example format (with string IDs)
      const option4 = [
        { probeId: "300100101013" },
        { probeId: "300110100038" },
        { probeId: "300110100039" }
      ];
      
      logger.log('Option 1 (simple IDs):', option1);
      logger.log('Option 2 (objects with probeId):', option2);
      logger.log('Option 3 (raw probes):', option3);
      logger.log('Option 4 (exact example format):', option4);
      
      // Use Option 2 with exact format that worked in Option 4
      // Fallback to Option 4 if no valid probe IDs found
      // const probesPayload = option2.length > 0 ? option2 : option4;
      
      // Use new teams data export instead of downloadAllData
      
      const exportPayload = {
        teamName: currentTeam.name,
        timeZone: filters.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone
      };
      
      // Reset any previous export state
      dispatch(resetTeamsExport());
      
      // Start the async teams data export
      await dispatch(startTeamsDataExport(exportPayload)).unwrap();
    } catch (error) {
      logger.error('Teams export failed:', error);
      dispatch(setTeamsExportError('Failed to start teams data export. Please try again.'));
    }
  };

  const handleAsyncMonthlyReport = async () => {
    if (!filters.asyncMonth) {
      dispatch(setAsyncReportError('Please select a month'));
      return;
    }

    // Validate and extract probes from scrollInfo.traps
    const rawProbes = scrollInfo?.traps || [];
    const probeIds = rawProbes
      .map((probe) => {
        const probeItem = probe as unknown as ProbeItem;
        const id = probeItem.trapId || probeItem.probeId || probeItem.id;
        return id ? String(id) : null;
      })
      .filter((id: string | null): id is string => id !== null);

    if (probeIds.length === 0) {
      dispatch(setAsyncReportError('No probes available. Please ensure you have probes configured for your team.'));
      return;
    }

    try {
      dispatch(resetAsyncReport());
      const payload = {
        month: convertMonthToEnglish(filters.asyncMonth), // Convert to English format for API
        probes: probeIds.map((id: string) => ({ probeId: id })),
        timeZone: filters.timeZone,
      };
      
      await dispatch(startAsyncMonthlyReport(payload)).unwrap();
    } catch (error) {
      logger.error('Failed to start async report:', error);
    }
  };

  const handleDownloadAsyncReport = async () => {
    if (!asyncReportState.jobId) return;
    
    try {
      await dispatch(downloadAsyncReport(asyncReportState.jobId)).unwrap();
    } catch (error) {
      logger.error('Download failed:', error);
    }
  };

  const handleDownloadTeamsExport = async () => {
    if (!teamsExportState.jobId) return;
    
    try {
      await dispatch(downloadAsyncReport(teamsExportState.jobId)).unwrap();
    } catch (error) {
      logger.error('Teams export download failed:', error);
    }
  };

  const generateAsyncMonthOptions = () => {
    // Generate last 12 months in "Nov 2024" format
    const options = [];
    const monthNames = getMonthNames();
    const now = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthStr = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
      options.push({
        value: monthStr,
        label: monthStr
      });
    }
    
    return options;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Navigation />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('dataDownload:downloadTitle')}</h2>
              <p className="text-gray-900">
                {t('dataDownload:downloadDescription')}
              </p>
              {!currentTeam && (
                <div className="mt-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded">
                  <p className="text-sm">Loading team information...</p>
                </div>
              )}
            </div>

            {/* Download Options */}
            <div className="space-y-6">
              {/* Date Range Selection */}
              <div>
                {SERVER_REGION !== 'CN' && (
                  <label className="block text-sm font-medium text-gray-900 mb-3">{t('dataDownload:downloadOptions')}</label>
                )}
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="dateRange"
                      value="all"
                      checked={filters.dateRange === 'all'}
                      onChange={(e) => setFilters({ ...filters, dateRange: e.target.value as 'all' | 'monthly' })}
                      className="mr-2"
                    />
                    <span>{t('dataDownload:exportTeamData')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="dateRange"
                      value="monthly"
                      checked={filters.dateRange === 'monthly'}
                      onChange={(e) => setFilters({ ...filters, dateRange: e.target.value as 'all' | 'monthly' })}
                      className="mr-2"
                    />
                    <span>{t('dataDownload:monthlyReportWithCharts')}</span>
                  </label>
                </div>
              </div>

              {/* Monthly Report Selection */}
              {filters.dateRange === 'monthly' && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-3">{t('dataDownload:monthlyReportWithCharts')}</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">{t('dataDownload:selectMonth')}</label>
                    <select
                      value={filters.asyncMonth}
                      onChange={(e) => setFilters({ ...filters, asyncMonth: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {generateAsyncMonthOptions().map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mt-3 p-2 bg-white border border-blue-200 rounded text-xs">
                    <div className="text-gray-900">
                      <div><strong>{t('dataDownload:selectedMonth')}:</strong> {filters.asyncMonth}</div>
                      <div className="mt-1"><strong>{t('dataDownload:probes')}:</strong> {scrollInfo?.traps?.length || 0} {t('dataDownload:available')}</div>
                    </div>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    {t('dataDownload:chartGenerationNote')}
                  </p>
                </div>
              )}

              {/* Async Report Progress */}
              {asyncReportState.isGenerating && asyncReportState.status && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <h4 className="text-sm font-medium text-green-900 mb-2">{t('dataDownload:reportGenerationProgress')}</h4>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${asyncReportState.status.progress || 0}%` }}
                    />
                  </div>
                  
                  <div className="flex justify-between text-xs text-green-700">
                    <span>{translateProgressMessage(asyncReportState.status.message)}</span>
                    <span>{Math.round(asyncReportState.status.progress || 0)}%</span>
                  </div>
                </div>
              )}

              {/* Async Report Complete */}
              {asyncReportState.status?.status === 'completed' && !asyncReportState.isGenerating && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <h4 className="text-sm font-medium text-green-900 mb-2">✅ {t('dataDownload:reportReady')}</h4>
                  <button
                    onClick={handleDownloadAsyncReport}
                    className="w-full py-2 px-4 bg-green-500 hover:bg-green-600 text-white rounded-md font-medium transition-colors"
                  >
                    {t('dataDownload:downloadAdvancedReport')}
                  </button>
                </div>
              )}

              {/* Error Display */}
              {(asyncReportState.error || asyncReportState.status?.status === 'failed') && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <h4 className="text-sm font-medium text-red-900 mb-2">⚠️ {t('dataDownload:error')}</h4>
                  <p className="text-sm text-red-700">
                    {asyncReportState.error || asyncReportState.status?.error || t('dataDownload:reportGenerationFailed')}
                  </p>
                  <button
                    onClick={() => dispatch(resetAsyncReport())}
                    className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                  >
                    {t('dataDownload:tryAgain')}
                  </button>
                </div>
              )}

              {/* Teams Export Progress */}
              {teamsExportState.isGenerating && teamsExportState.status && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <h4 className="text-sm font-medium text-green-900 mb-2">{t('dataDownload:teamDataExportProgress')}</h4>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${teamsExportState.status.progress || 0}%` }}
                    />
                  </div>
                  
                  <div className="flex justify-between text-xs text-green-700">
                    <span>{translateProgressMessage(teamsExportState.status.message)}</span>
                    <span>{Math.round(teamsExportState.status.progress || 0)}%</span>
                  </div>
                </div>
              )}

              {/* Teams Export Complete */}
              {teamsExportState.status?.status === 'completed' && !teamsExportState.isGenerating && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <h4 className="text-sm font-medium text-green-900 mb-2">✅ {t('dataDownload:teamDataExportReady')}</h4>
                  <p className="text-sm text-green-700 mb-3">
                    {t('dataDownload:teamDataExportReadyDescription')}
                  </p>
                  <button
                    onClick={handleDownloadTeamsExport}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    {t('dataDownload:downloadTeamDataExport')}
                  </button>
                </div>
              )}

              {/* Teams Export Error */}
              {(teamsExportState.error || teamsExportState.status?.status === 'failed') && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <h4 className="text-sm font-medium text-red-900 mb-2">⚠️ {t('dataDownload:exportError')}</h4>
                  <p className="text-sm text-red-700">
                    {teamsExportState.error || teamsExportState.status?.error || t('dataDownload:teamDataExportFailed')}
                  </p>
                  <button
                    onClick={() => dispatch(resetTeamsExport())}
                    className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                  >
                    {t('dataDownload:tryAgain')}
                  </button>
                </div>
              )}

              {/* Download Button */}
              <div className="pt-6 border-t border-gray-200">
                {filters.dateRange === 'monthly' ? (
                  <button
                    onClick={handleAsyncMonthlyReport}
                    disabled={asyncReportState.isGenerating || !currentTeam?.name || !filters.asyncMonth}
                    className={`w-full py-3 px-4 rounded-md font-medium text-white transition-colors ${
                      asyncReportState.isGenerating || !currentTeam?.name || !filters.asyncMonth
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-500 hover:bg-blue-600 cursor-pointer'
                    }`}
                  >
                    {asyncReportState.isGenerating ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                        {t('dataDownload:generatingReport')}
                      </div>
                    ) : (
                      t('dataDownload:generateMonthlyReport')
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleDownload}
                    disabled={teamsExportState.isGenerating || !currentTeam?.name}
                    className={`w-full py-3 px-4 rounded-md font-medium text-white transition-colors ${
                      teamsExportState.isGenerating || !currentTeam?.name
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-500 hover:bg-blue-600 cursor-pointer'
                    }`}
                  >
                    {teamsExportState.isGenerating ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                        {t('dataDownload:generatingExport')}
                      </div>
                    ) : (
                      t('dataDownload:exportTeamData')
                    )}
                  </button>
                )}
              </div>

              {/* Information Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2">{t('dataDownload:downloadInformation')}</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• <strong>{t('dataDownload:allData')}:</strong> {t('dataDownload:allDataDescription')}</li>
                  <li>• <strong>{t('dataDownload:monthlyReportWithCharts')}:</strong> {t('dataDownload:monthlyReportDescription')}</li>
                  <li>• {t('dataDownload:largeDatasetNote')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
